import { isSuperAdminUser } from "./centerAccessUtils";

const CRUD_ACTIONS = ["create", "read", "update", "delete"];

/**
 * Merge permission objects across roles.
 * Rule: true overrides false; false never overrides true.
 */
export function mergePermissionObjects(permissionSets = []) {
  const merged = {};

  permissionSets.forEach((permissions) => {
    if (!permissions || typeof permissions !== "object") return;

    Object.entries(permissions).forEach(([moduleName, actions]) => {
      if (!actions || typeof actions !== "object") return;
      if (!merged[moduleName]) {
        merged[moduleName] = {
          create: false,
          read: false,
          update: false,
          delete: false,
        };
      }
      CRUD_ACTIONS.forEach((action) => {
        merged[moduleName][action] =
          merged[moduleName][action] || Boolean(actions[action]);
      });
    });
  });

  return merged;
}

/**
 * Management Access (settings) grants read/create/update/delete on management modules
 * according to each action on settings — View (read-only) does not imply create or update.
 * Mirrors backend authorize + mergeRolePermissions enrichment.
 */
export function enrichManagementPermissions(permissions = {}) {
  const settings = permissions.settings || {};
  if (!settings.read) {
    return permissions;
  }

  const next = { ...permissions };
  for (const module of [
    "users",
    "roles",
    "devices",
    "medications",
    "medication_frequencies",
    "medication_hold_timings",
    "insurances",
    "indications",
    "complications",
  ]) {
    const current = permissions[module] || {};
    next[module] = {
      ...current,
      read: current.read || settings.read,
      create: current.create || settings.create,
      update: current.update || settings.update,
      delete: current.delete || settings.delete,
    };
  }
  return next;
}

export function getEffectivePermissions(currentUser) {
  if (!currentUser?.permissions && !currentUser?.roles?.length) return {};

  // Prefer backend-merged permissions; if roles include structured permissions, OR-merge them in
  // so any true from an assigned role is preserved.
  const rolePermissionSets = (currentUser?.roles || [])
    .map((role) => role?.permissions)
    .filter(
      (permissions) =>
        permissions &&
        typeof permissions === "object" &&
        !Array.isArray(permissions)
    );

  const basePermissions =
    rolePermissionSets.length > 0
      ? mergePermissionObjects([
          currentUser.permissions || {},
          ...rolePermissionSets,
        ])
      : currentUser.permissions || {};

  if (isSuperAdminUser(currentUser)) {
    return basePermissions;
  }
  return enrichManagementPermissions(basePermissions);
}

export function hasManagementModulePermission(currentUser, module, action) {
  if (isSuperAdminUser(currentUser)) return true;
  const permissions = getEffectivePermissions(currentUser);
  return Boolean(permissions?.[module]?.[action]);
}

/**
 * Creating or editing role definitions requires Full Management Access.
 * That maps to settings.create, which enriches onto roles.create.
 * Partial Management Access is read+update only — not enough to modify roles.
 */
export function canModifyRoleDefinitions(currentUser) {
  if (isSuperAdminUser(currentUser)) return true;
  const permissions = getEffectivePermissions(currentUser);
  return Boolean(
    permissions?.roles?.create || permissions?.settings?.create
  );
}

/** Whether the session may call GET /messages/unread-count (Communication or Inbox). */
export function canFetchMessagesUnreadCount(currentUser) {
  if (isSuperAdminUser(currentUser)) return true;
  const p = currentUser?.permissions || {};
  return Boolean(
    p.messages?.read ||
      p.communication_modes?.read ||
      p.communicationModes?.read ||
      p.inbox?.read
  );
}
